<script>
import Vue from "vue/dist/vue.esm";
// import VueRouter from "vue-router";
import { mapState, mapGetters } from "vuex";
import _ from "lodash";
import VueSticky from "vue-sticky";
import ReportSettings from "views/reports/settings.vue";
import ReportCards from "./components/ReportCards.vue";
// import ReportTable from "./components/ReportTable.vue";
import ReportCharts from "./components/ReportCharts.vue";
import ReportHeader from "./components/header.vue";
import ReportMenu from "./components/reportMenu.vue";
import GroupHeader from "./components/groupHeader.vue";
import ReportContentSources from "./components/ReportContentSources.vue";
import EventBus from "components/eventbus";
import ReportSummaryMetrics from "./components/ReportSummaryMetrics.vue";
import SearchTagBar from "./components/searchTagBar.vue";
import { chosenChartMetrics } from "lib/reportChartCatalog.mjs";

// Vue.use(VueRouter);

// const routes = [
//   { path: "/cards", component: ReportCards, props: {} },
//   { path: "/table", component: ReportTable, props: {} },
//   { path: "*", redirect: "/cards" }
// ];
// const router = new VueRouter({ routes, linkActiveClass: "active" });

export default {
  components: {
    ReportCards,
    // ReportTable,
    ReportCharts,
    ReportHeader,
    ReportMenu,
    ReportSettings,
    GroupHeader,
    ReportSummaryMetrics,
    ReportContentSources,
    SearchTagBar,
  },
  directives: {
    sticky: VueSticky,
  },
  props: {
    resource: Object,
    report: Object,
    resourcePath: String,
    tenantLogo: String,
    linechart: Boolean,
    deliverables: Array,
    editable: false,
    hideImpressionsChart: {
      type: Boolean,
      default: false,
    },
    hideViewsChart: {
      type: Boolean,
      default: false,
    },
    hideEngagementsChart: {
      type: Boolean,
      default: false,
    },
    lazyLoad: {
      type: Boolean,
      default: true,
    },
    expandStories: {
      type: Boolean,
      default: false,
    },
    pdfReport: {
      type: Boolean,
      default: false,
    },
    campaignCost: {
      type: Object,
      default: () => ({}),
    },
    // Report#chart_metrics: the donuts this saved report draws. Only the saved
    // report surfaces (reports/show, the share link, the PDF) pass it; the
    // calendar and event reports and the deliverables list leave it null and
    // keep drawing the workspace default from the hide-*-chart flags above.
    chartMetrics: {
      type: Array,
      default: null,
    },
  },
  data() {
    const sections = (this.report && this.report.sections) || {};
    return {
      typeIndex: {},
      selectedDeliverableTypes: [],
      searchQuery: "",
      // Ids the server says match the search, keyed by id. The server also
      // searches article text, which never reaches the browser. serverTerm is
      // the term those ids answer, so a stale response can't apply to a new one.
      serverMatches: {},
      serverTerm: "",
      // Client-side, deliverable-level tag filter (chips + multi-select).
      selectedTags: [],
      // Grouping + sorting. Initialized from the report's owner-set defaults
      // (report.sections.*); an owner changing them saves a new default, while
      // a share-link viewer's change only affects their own session.
      groupMode: sections.default_group_mode || "none",
      sortMode: sections.default_sort || "performance:desc",
      collapsedGroups: [],
    };
  },
  computed: {
    // The owner's live pick wins over the server-resolved list, so ticking a
    // chart in Choose metrics redraws the donuts without a reload.
    reportChartMetrics() {
      if (!this.chartMetrics) return null;
      return chosenChartMetrics(this.report && this.report.sections, this.chartMetrics);
    },
    ...mapState("report", [
      "loading",
      "loaded",
      "cacheMeta",
      "loadError",
      "report.sections.removeDuplicates",
      // Per-tenant gate for the deliverable-tags UI, seeded by the share view
      // from the report owner's tenant flag (default false).
      "deliverableTagsEnabled",
    ]),
    cacheBannerVisible() {
      return (
        this.cacheMeta &&
        (this.cacheMeta.freshness === "stale" ||
          this.cacheMeta.freshness === "warming")
      );
    },
    cacheWarming() {
      return this.cacheMeta && this.cacheMeta.freshness === "warming";
    },
    cacheBuiltAtRelative() {
      if (!this.cacheMeta || !this.cacheMeta.built_at) return null;
      const built = new Date(this.cacheMeta.built_at);
      const minutes = Math.round((Date.now() - built.getTime()) / 60000);
      if (minutes < 1) return "just now";
      if (minutes < 60) return `${minutes} min ago`;
      const hours = Math.round(minutes / 60);
      if (hours < 24) return `${hours} hr ago`;
      return `${Math.round(hours / 24)} days ago`;
    },
    ...mapGetters("report", ["hasFilters"]),
    ...mapGetters("deliverable", {
      typeAsIconClass: "inferredTypeAsIconClass",
    }),
    ...mapState("deliverable", ["colors"]),

    lastUpdated() {
      if (this.sortedDeliverables.length > 0) {
        const lastFetched = _.compact(
          _.map(this.sortedDeliverables, "last_fetched")
        );
        const date = _.reverse(lastFetched.sort())[0];

        if (date) {
          return moment(date).format("MM/DD/Y hh:mm");
        }
        return false;
      }
      return "";
    },
    filteredDeliverables() {
      const that = this;
      return this.deliverables.filter((d) => that.shouldShow(d));
    },
    sortedDeliverables() {
      const $this = this;
      let dels = this.filteredDeliverables;

      if ($this.report && $this.report.sections.removeDuplicates) {
        const deliverablesMap = new Map();

        this.filteredDeliverables.forEach((del) => {
          const existing = deliverablesMap.get(del.url);
          if (
            !existing ||
            new Date(del.created_at) > new Date(existing.created_at)
          ) {
            deliverablesMap.set(del.url, del);
          }
          dels = Array.from(deliverablesMap.values());
        });
      }

      const dir = this.sortMode.split(":")[1] === "asc" ? "asc" : "desc";
      return _.orderBy(dels, [(del) => $this.sortValueFor(del)], [dir]).map(
        (del) => {
          del.iconClass = $this.typeAsIconClass(del.inferred_type);
          return del;
        }
      );
    },
    deliverableTypes() {
      const that = this;
      that.typeIndex = {};
      const a = this.deliverables.map((d) => {
        const base = that.typeIndex;
        const type = d.inferred_type;
        if (!base[type]) {
          base[type] = 0;
        }
        that.typeIndex[type] += 1;
        return type;
      });
      return Array.from(new Set(a)).map((type) => ({
        type,
        iconClass: this.typeAsIconClass(type),
        label: this.typeAsLabel(type),
      }));
    },
    isMobile() {
      return window.innerWidth <= 768;
    },
    // Owners can turn a search into a tag, so the report can be filtered or
    // grouped by it later. Not for share-link viewers.
    canTagSearchResults() {
      return (
        this.editable &&
        this.deliverableTagsEnabled &&
        this.searchQuery.trim() !== "" &&
        this.sortedDeliverables.length > 0
      );
    },
    // All distinct deliverable-level tags present in this report, sorted.
    // Gated by the per-tenant flag: when the feature is off this returns [],
    // which hides the Tags filter dropdown (reportMenu v-if) and the
    // "Deliverable tag" group option in one place, and makes chip-click filters
    // no-ops — so a flag-off report is unchanged even if deliverables carry
    // tags set via MCP.
    allTags() {
      if (!this.deliverableTagsEnabled) return [];
      const set = new Set();
      this.deliverables.forEach((d) => {
        (d.tag_list || []).forEach((t) => set.add(t));
      });
      return Array.from(set).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      );
    },
    // The grouping dimension actually applied. Falls back to "none" when the
    // tag feature is off but a stale owner default still says "deliverable_tag"
    // (flag was on, a default was saved, then the flag was turned off) — so no
    // tag grouping or tag group headers ever render while the flag is off.
    activeGroupMode() {
      if (
        this.groupMode === "deliverable_tag" &&
        !this.deliverableTagsEnabled
      ) {
        return "none";
      }
      return this.groupMode;
    },
    isGrouped() {
      return this.activeGroupMode && this.activeGroupMode !== "none";
    },
    // Options surfaced in the Group ▾ control. "No grouping" keeps the report's
    // existing per-creator grouping on multi-creator reports. "Deliverable tag"
    // only appears when this report actually has tags.
    groupOptions() {
      const opts = [{ label: "No grouping", value: "none" }];
      if (this.allTags.length) {
        opts.push({ label: "Deliverable tag", value: "deliverable_tag" });
      }
      opts.push({ label: "Channel", value: "channel" });
      return opts;
    },
    sortOptions() {
      return [
        { label: "Top performing", value: "performance:desc" },
        { label: "Impressions", value: "impressions:desc" },
        { label: "Reach", value: "impressions_unique:desc" },
        { label: "Views", value: "views:desc" },
        { label: "Engagements", value: "engagements:desc" },
        { label: "Newest first", value: "published:desc" },
        { label: "Oldest first", value: "published:asc" },
        { label: "Title (A–Z)", value: "name:asc" },
      ];
    },
    groupIcon() {
      switch (this.activeGroupMode) {
        case "deliverable_tag":
          return "tag";
        case "channel":
          return "th large";
        default:
          return "folder";
      }
    },
    emptyGroupLabel() {
      return this.activeGroupMode === "deliverable_tag" ? "Untagged" : "Other";
    },
    // Bucket the already-filtered+sorted deliverables by the active group
    // dimension, with the empty bucket ("Untagged"/"Other") always last.
    // Filtering narrows the set, sorting orders within, grouping buckets — they
    // compose in that order.
    groups() {
      const buckets = new Map();
      const noneBucket = [];
      this.sortedDeliverables.forEach((del) => {
        const key = this.groupKeyFor(del);
        if (key === null || key === undefined || key === "") {
          noneBucket.push(del);
          return;
        }
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(del);
      });
      const groups = Array.from(buckets.keys())
        .sort((a, b) =>
          String(this.groupLabelFor(a)).localeCompare(
            String(this.groupLabelFor(b)),
            undefined,
            { sensitivity: "base" }
          )
        )
        .map((key) => ({
          key,
          label: this.groupLabelFor(key),
          count: buckets.get(key).length,
          deliverables: buckets.get(key),
          empty: false,
        }));
      if (noneBucket.length) {
        groups.push({
          key: "__none__",
          label: this.emptyGroupLabel,
          count: noneBucket.length,
          deliverables: noneBucket,
          empty: true,
        });
      }
      return groups;
    },
  },
  mounted() {
    this.$nextTick(() => {
      // this.$store.dispatch("report/loadDeliverables");
    });
    EventBus.$on("filterByDeliverableTag", this.onTagClickedFromCard);
  },
  watch: {
    cacheWarming(now) {
      if (now) this.startWarmingPoll();
    },
    // The report loads asynchronously (store SHOW replaces report after this
    // component is created), so adopt the owner-set defaults once they arrive.
    // report.sections.* only changes on an owner save, so a viewer's in-session
    // override is never clobbered by this.
    "report.sections.default_group_mode"(val) {
      if (val) this.groupMode = val;
    },
    "report.sections.default_sort"(val) {
      if (val) this.sortMode = val;
    },
  },
  beforeDestroy() {
    if (this._warmingTimer) {
      clearInterval(this._warmingTimer);
      this._warmingTimer = null;
    }
    EventBus.$off("filterByDeliverableTag", this.onTagClickedFromCard);
  },
  created() {
    this.runServerSearch = _.debounce(this.fetchServerMatches, 400);
  },
  methods: {
    setSearchQuery(val) {
      this.searchQuery = val;
      this.runServerSearch(val.trim());
    },
    fetchServerMatches(term) {
      if (term.length < 3 || !this.report || !this.report.id) {
        this.serverMatches = {};
        this.serverTerm = "";
        return;
      }
      this.$store
        .dispatch("report/searchDeliverableIds", term)
        .then((ids) => {
          if (term !== this.searchQuery.trim()) return;
          const matches = {};
          ids.forEach((id) => {
            matches[id] = true;
          });
          this.serverMatches = matches;
          this.serverTerm = term.toLowerCase();
        })
        .catch(() => {
          // The local match still works; the article-text match is a bonus.
        });
    },
    // Tag exactly the cards on screen: sortedDeliverables, which also applies
    // "remove duplicates". The bar's count reads the same list.
    tagSearchResults(tag) {
      const ids = this.sortedDeliverables.map((d) => d.id);
      return this.$store.dispatch("report/bulkTagDeliverables", { ids, tag });
    },
    deliverableTypesChanged(value) {
      this.selectedDeliverableTypes = value.split(",").filter((e) => e);
    },
    // typeAsIconClass(type) {
    //   switch (type) {
    //     case "url":
    //       return "linkify";
    //     case "bitly":
    //       return "linkify";
    //     case "mailchimp":
    //     case "convertkit":
    //     case "sendfox":
    //     case "aweber":
    //     case "activecampaign":
    //       return "envelope";
    //     case "other":
    //       return "sticky note outline";
    //     case "instagram_stories":
    //       return "instagram";
    //     case "push_engage":
    //       return "bullhorn";
    //     default:
    //       return type;
    //   }
    // },
    typeAsLabel(type) {
      switch (type) {
        case "url":
          return "Articles";
        case "instagram_stories":
          return "Instagram Stories";
        case "one_signal":
          return "Push Notification (OneSignal)";
        case "push_engage":
          return "Push Notification (PushEngage)";

        default:
          return type[0].toUpperCase() + type.slice(1);
      }
    },
    reloadForFreshData() {
      this.$store.dispatch("report/loadDeliverables");
    },
    startWarmingPoll() {
      if (this._warmingTimer) return;
      this._warmingTimer = setInterval(() => {
        if (!this.cacheWarming) {
          clearInterval(this._warmingTimer);
          this._warmingTimer = null;
          return;
        }
        this.$store.dispatch("report/loadDeliverables");
      }, 15000);
    },
    shouldShow(deliverable) {
      const q = this.searchQuery.trim().toLowerCase();
      if (
        q !== "" &&
        q === this.serverTerm &&
        this.serverMatches[deliverable.id]
      ) {
        return this.passesTagAndTypeFilters(deliverable);
      }
      return this.matchesLocally(deliverable, q);
    },
    passesTagAndTypeFilters(deliverable) {
      const tags = deliverable.tag_list || [];
      return (
        (this.selectedTags.length < 1 ||
          tags.some((t) => this.selectedTags.includes(t))) &&
        (this.selectedDeliverableTypes.length < 1 ||
          this.selectedDeliverableTypes.includes(
            deliverable.inferred_type.toString()
          ))
      );
    },
    matchesLocally(deliverable, q) {
      return (
        this.passesTagAndTypeFilters(deliverable) &&
        (q === "" ||
          String(deliverable.name).toLowerCase().indexOf(q) > -1 ||
          String(deliverable.description).toLowerCase().indexOf(q) > -1 ||
          String(deliverable.user.login).toLowerCase().indexOf(q) > -1 ||
          String(deliverable.url).toLowerCase().indexOf(q) > -1)
      );
    },
    toggleTag(tag) {
      const i = this.selectedTags.indexOf(tag);
      if (i > -1) {
        this.selectedTags.splice(i, 1);
      } else {
        this.selectedTags.push(tag);
      }
    },
    clearTagFilter() {
      this.selectedTags = [];
    },
    setSelectedTags(value) {
      // From the Tags ▾ multi-select in the control bar (comma-joined string).
      this.selectedTags =
        typeof value === "string"
          ? value.split(",").filter((t) => t)
          : value || [];
    },
    onTagClickedFromCard(tag) {
      // Clicking a chip on a card toggles it into the active filter.
      if (this.allTags.includes(tag)) {
        this.toggleTag(tag);
      }
    },
    toggleGroup(key) {
      const i = this.collapsedGroups.indexOf(key);
      if (i > -1) this.collapsedGroups.splice(i, 1);
      else this.collapsedGroups.push(key);
    },
    isGroupCollapsed(key) {
      return this.collapsedGroups.includes(key);
    },
    statValue(deliverable, type) {
      const stat = (deliverable.statisticsValues || []).find(
        (s) => s.type === type
      );
      return stat && stat.total ? Number(stat.total.value) || 0 : 0;
    },
    sortValueFor(deliverable) {
      switch (this.sortMode.split(":")[0]) {
        case "impressions":
          return this.statValue(deliverable, "impressions");
        case "impressions_unique":
          return this.statValue(deliverable, "impressions_unique");
        case "views":
          return this.statValue(deliverable, "views");
        case "engagements":
          return this.statValue(deliverable, "engagements");
        case "published":
          return deliverable.provider_published_at
            ? new Date(deliverable.provider_published_at).getTime()
            : 0;
        case "name":
          return String(deliverable.name || "").toLowerCase();
        case "performance":
        default:
          return deliverable.sortableStatisticValue
            ? Number(deliverable.sortableStatisticValue.value) || 0
            : 0;
      }
    },
    groupKeyFor(deliverable) {
      switch (this.activeGroupMode) {
        case "deliverable_tag":
          return (deliverable.tag_list && deliverable.tag_list[0]) || null;
        case "channel":
          return deliverable.inferred_type || null;
        case "creator":
          return (
            deliverable.user_login ||
            (deliverable.user && deliverable.user.login) ||
            null
          );
        default:
          return null;
      }
    },
    groupLabelFor(key) {
      return this.activeGroupMode === "channel" ? this.typeAsLabel(key) : key;
    },
    setGroupMode(mode) {
      this.groupMode = mode;
      this.persistDefault("default_group_mode", mode);
    },
    setSortMode(value) {
      this.sortMode = value;
      this.persistDefault("default_sort", value);
    },
    // Owners persist the choice as the report's default; share-link viewers
    // keep it to their own session only (no write — guarded by `editable`,
    // which is also enforced server-side by ReportPolicy#update?).
    persistDefault(key, value) {
      if (!this.editable || !this.report || !this.report.id) return;
      if (!this.report.sections) this.$set(this.report, "sections", {});
      this.$set(this.report.sections, key, value);
      this.$store.dispatch("report/save", {
        id: this.report.id,
        name: this.report.name,
        sections: this.report.sections,
      });
    },
  },
};
</script>

<template>
  <div class="ui container">
    <report-settings v-if="editable && !report.reportable_id" />

    <report-header
      :key="report.id"
      :last-updated="lastUpdated"
      :resource="resource"
      :initial-report="report"
      :resource-path="resourcePath"
      :initial-tenant-logo="tenantLogo"
      :editable="editable"
      :deliverable-types="deliverableTypes"
      :type-index="typeIndex"
      :colors="colors"
      @reportFilterChanged="deliverableTypesChanged"
    />

    <div
      v-if="cacheBannerVisible"
      class="ui info icon message stats-cache-banner"
    >
      <i class="sync alternate icon" />
      <div v-if="cacheWarming" class="content">
        <div class="header">Preparing your report</div>
        <p>
          This is a large report — we're building it for the first time. It
          usually takes about a minute. We'll auto-refresh when it's ready.
        </p>
      </div>
      <div v-else class="content">
        <div class="header">
          Showing cached data from {{ cacheBuiltAtRelative }}
        </div>
        <p>
          We're refreshing in the background.
          <a href="#" @click.prevent="reloadForFreshData"
            >Reload for fresh data</a
          >
        </p>
      </div>
    </div>

    <div v-if="loading" class="ui flat segment" style="height: 400px">
      <div class="ui active inverted dimmer">
        <div class="ui text loader">Building report</div>
      </div>
      <p />
    </div>

    <div v-if="!loading" class="ui report-main">
      <!--
        Report Builder filters deliverables that are ALREADY tracked, and said
        so only as "After applying some filters, your report will be generated
        here." — which reads as an instruction that will work. For an account
        with nothing tracked yet it is a dead end with no exit: a brand spent
        two and a half minutes here clicking filters, asked Kit "How to add
        content to report", and never came back. Both states now say where
        content comes from and offer the two routes to it.
      -->
      <!--
        A failed load is not an empty report. Before this branch existed, a
        rack-timeout on a large report fell through to "Add a filter above and
        your report will be generated here" — so the one screen that had to say
        "we could not build this" instead told the owner their content was
        missing. Report 72272 (748 deliverables) took 32.1s to build against a
        30s timeout, and that is what its owner saw.
      -->
      <div v-if="deliverables.length < 1 && loadError" class="ui message">
        <div class="ui basic center aligned segment">
          <div class="header">
            {{
              loadError.timeout
                ? "This report took too long to load"
                : "We couldn't load this report"
            }}
          </div>
          <p v-if="loadError.timeout">
            Your content is still here — the report just didn't finish building
            in time. Large reports are cached after their first successful
            build, so a retry usually works.
          </p>
          <p v-else>
            Something went wrong on our end. Your content is still here.
          </p>
          <button class="ui white bordered button" @click="reloadForFreshData">
            Try again
          </button>
        </div>
      </div>

      <div v-else-if="deliverables.length < 1" class="ui message">
        <div
          v-if="!hasFilters || (hasFilters && !loaded)"
          class="ui basic center aligned segment"
        >
          <p>Add a filter above and your report will be generated here.</p>
          <report-content-sources />
        </div>
        <div v-else class="ui basic center aligned segment">
          <div class="header">No content matched those filters</div>
          <report-content-sources />
        </div>
      </div>

      <!-- No deliverables guard: the panel carries the report's notes as well
           as its metrics, and a report with notes and no content still has to
           show them. The component decides for itself whether it has anything
           worth rendering. -->
      <report-summary-metrics
        :deliverables="filteredDeliverables"
        :report="report"
        :editable="editable"
        :campaign-cost="campaignCost"
        :chart-metrics="reportChartMetrics"
      />

      <report-charts
        v-if="deliverables.length > 0"
        :deliverables="filteredDeliverables"
        :colors="colors"
        :linechart="linechart"
        :hide-impressions-chart="hideImpressionsChart"
        :hide-views-chart="hideViewsChart"
        :hide-engagements-chart="hideEngagementsChart"
        :chart-metrics="reportChartMetrics"
      />

      <!-- ui bound sticky -->
      <div
        v-if="deliverables.length > 0"
        class="ui menu-bottom desktop-only"
        :class="{
          sticky: !isMobile,
          'sticky-filter-search': !isMobile,
        }"
      >
        <report-menu
          :deliverable-types="deliverableTypes"
          :selected-deliverable-types="selectedDeliverableTypes"
          :type-index="typeIndex"
          :colors="colors"
          :tags="allTags"
          :selected-tags="selectedTags"
          :group-mode="activeGroupMode"
          :sort-mode="sortMode"
          :group-options="groupOptions"
          :sort-options="sortOptions"
          :editable="editable"
          @reportFilterChanged="deliverableTypesChanged"
          @tagsChanged="setSelectedTags"
          @changeSearchQuery="setSearchQuery"
          @groupChanged="setGroupMode"
          @sortChanged="setSortMode"
        />
      </div>

      <search-tag-bar
        v-if="canTagSearchResults"
        :term="searchQuery.trim()"
        :count="sortedDeliverables.length"
        :on-tag="tagSearchResults"
      />

      <div v-if="isGrouped">
        <div
          v-for="g in groups"
          :key="g.key"
          class="deliverable-tag-group ui segment basic"
        >
          <group-header
            :title="g.label"
            :deliverables="g.deliverables"
            :count="g.count"
            :icon="g.empty ? '' : groupIcon"
            :collapsible="groups.length > 1"
            :collapsed="isGroupCollapsed(g.key)"
            @toggle="toggleGroup(g.key)"
          />
          <report-cards
            v-show="!isGroupCollapsed(g.key)"
            :deliverables="g.deliverables"
            :lazy-load="lazyLoad"
            :expand-stories="expandStories"
            :pdf-report="pdfReport"
            :disable-user-grouping="true"
          />
        </div>
      </div>

      <keep-alive v-else>
        <report-cards
          :deliverables="this.sortedDeliverables"
          :lazy-load="lazyLoad"
          :expand-stories="expandStories"
          :pdf-report="pdfReport"
        />
      </keep-alive>
    </div>
  </div>
</template>

<style lang="css" scoped>
.sticky-filter-search {
  position: sticky !important;
  top: 0px;
  z-index: 1000;
}

.deliverable-tag-group {
  margin-bottom: 1.5rem;
  padding: 0;
}
</style>
